import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabaseClient";
import ALL_OCCUPATIONS from "./data/allMilitaryOccupations";

const SIGN_IN_MESSAGE = 'Sign in with your member account to explore civilian careers.';
const UNAVAILABLE_MESSAGE = 'Career exploration is unavailable right now. Please try again later.';

// Authenticated server-side translation; privileged credentials never enter this bundle.
async function callClaude(code, branch, title) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error(SIGN_IN_MESSAGE);
  const { data, error } = await supabase.functions.invoke('skill-translate', { body: { code, branch, title } });
  if (error || !data || data.error) throw new Error(UNAVAILABLE_MESSAGE);
  return data;
}

// ─── Autocomplete helpers ─────────────────────────────────────────────────────
const BRANCH_ORDER = ["Air Force", "Army", "Marine Corps", "Navy", "Space Force", "Coast Guard"];

function searchOccupations(query, maxResults = 12) {
  if (!query || query.trim().length < 1) return [];
  const q = query.trim().toLowerCase();
  const scored = [];

  for (const occ of ALL_OCCUPATIONS) {
    const codeL = occ.code.toLowerCase();
    const titleL = occ.title.toLowerCase();
    let score = 0;

    if (codeL === q) score = 100;
    else if (codeL.startsWith(q)) score = 80 + (20 - Math.min(20, occ.code.length));
    else if (titleL.startsWith(q)) score = 70;
    else if (codeL.includes(q)) score = 50;
    else if (titleL.includes(q)) score = 40;
    else continue;

    scored.push({ ...occ, _score: score });
  }

  scored.sort((a, b) => {
    if (b._score !== a._score) return b._score - a._score;
    return BRANCH_ORDER.indexOf(a.branch) - BRANCH_ORDER.indexOf(b.branch);
  });

  return scored.slice(0, maxResults);
}

// ─── Quick examples ───────────────────────────────────────────────────────────
const EXAMPLES = [
  { code: "1B4X1", branch: "Air Force",    title: "Cyberspace Operations" },
  { code: "35F",   branch: "Army",         title: "Intelligence Analyst"  },
  { code: "25B",   branch: "Army",         title: "IT Specialist"         },
  { code: "68W",   branch: "Army",         title: "Combat Medic"          },
  { code: "IT",    branch: "Navy",         title: "Information Systems Tech" },
  { code: "0651",  branch: "Marine Corps", title: "Cyber Network Operator"},
];

// ─── Category color map ───────────────────────────────────────────────────────
const CAT_STYLE = {
  "Enlisted":        { bg: "rgba(99,179,237,0.12)",  border: "rgba(99,179,237,0.3)",  color: "#63b3ed" },
  "Officer":         { bg: "rgba(201,168,76,0.12)",  border: "rgba(201,168,76,0.3)",  color: "#C9A84C" },
  "Warrant Officer": { bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.3)", color: "#a78bfa" },
};

// ─────────────────────────────────────────────────────────────────────────────
export default function VetSkillEngine() {
  const [query, setQuery]             = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected]       = useState(null);
  const [dropOpen, setDropOpen]       = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState("jobs");
  const [revealedBullet, setRevealedBullet] = useState(null);
  const [copied, setCopied]           = useState(null);

  const inputRef  = useRef(null);
  const wrapRef   = useRef(null);
  const resultRef = useRef(null);
  const requestInFlight = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Live search on every keystroke
  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); setDropOpen(false); setHighlightIdx(-1); return; }
    const results = searchOccupations(query);
    setSuggestions(results);
    setDropOpen(results.length > 0);
    setHighlightIdx(-1);
  }, [query]);

  const selectSuggestion = useCallback((occ) => {
    setSelected(occ);
    setQuery(`${occ.code} — ${occ.title}`);
    setDropOpen(false);
    setSuggestions([]);
    setHighlightIdx(-1);
  }, []);

  const clearInput = () => {
    setQuery(""); setSelected(null); setSuggestions([]);
    setDropOpen(false); inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!dropOpen || suggestions.length === 0) {
      if (e.key === "Enter" && selected) handleTranslate();
      return;
    }
    if (e.key === "ArrowDown")  { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const pick = highlightIdx >= 0 ? suggestions[highlightIdx] : suggestions[0];
      if (pick) selectSuggestion(pick);
    }
    else if (e.key === "Escape") setDropOpen(false);
  };

  const handleTranslate = async (overrideOcc) => {
    const use = overrideOcc || selected;
    if (!use || requestInFlight.current) return;
    requestInFlight.current = true;
    setLoading(true); setError(null); setResult(null);
    setActiveTab("jobs"); setRevealedBullet(null);
    try {
      const data = await callClaude(use.code, use.branch, use.title);
      setResult(data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      setError(err?.message === SIGN_IN_MESSAGE ? SIGN_IN_MESSAGE : UNAVAILABLE_MESSAGE);
    }
    requestInFlight.current = false;
    setLoading(false);
  };

  const handleExample = (ex) => {
    if (requestInFlight.current) return;
    const occ = ALL_OCCUPATIONS.find(o => o.code === ex.code && o.branch === ex.branch)
      || { ...ex, category: "Enlisted" };
    selectSuggestion(occ);
    handleTranslate(occ);
  };

  const handleCopy = (text, idx, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  // Bold the matched portion of text in dropdown
  const highlight = (text, q) => {
    if (!q || !q.trim()) return text;
    const raw = q.trim();
    const idx = text.toLowerCase().indexOf(raw.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={S.mark}>{text.slice(idx, idx + raw.length)}</mark>
        {text.slice(idx + raw.length)}
      </>
    );
  };

  const tabs = [
    { id: "jobs",      label: "Job Matches"       },
    { id: "certs",     label: "Certifications"    },
    { id: "employers", label: "Employers"          },
    { id: "resume",    label: "Resume Translator" },
  ];

  return (
    <div style={S.root}>
      <div style={S.bg} />

      {/* ── HEADER ── */}
      <div style={S.header}>
        <div style={S.headerInner}>
          <div style={S.engineBadge}>INTELLIGENCE ENGINE</div>
          <h1 style={S.h1}>Veteran Job‑Skill<br /><em style={S.h1em}>Intelligence Engine</em></h1>
          <p style={S.subtitle}>
            Search any MOS, AFSC, NEC, or Rating — enlisted or officer, all six branches.
            Explore possible civilian roles, credentials to research, and resume templates. Member sign-in required.
          </p>
        </div>
      </div>

      {/* ── SEARCH ── */}
      <div style={S.searchSection}>
        <div style={S.searchCard}>

          <div style={S.searchRow}>
            {/* AUTOCOMPLETE FIELD */}
            <div style={S.inputWrap} ref={wrapRef}>
              <label style={S.inputLabel}>
                Search — All Branches · Enlisted &amp; Officer · {ALL_OCCUPATIONS.length.toLocaleString()} Occupations
              </label>
              <div style={S.inputGroup}>
                <span style={S.searchIconWrap}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8FA3BE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={inputRef}
                  style={S.input}
                  value={query}
                  onChange={e => { setQuery(e.target.value); setSelected(null); }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => suggestions.length > 0 && setDropOpen(true)}
                  placeholder="Type a code or job title… e.g. 1B4X1, Cavalry Scout, Corpsman"
                  autoComplete="off"
                  spellCheck={false}
                />
                {query && (
                  <button style={S.clearBtn} onClick={clearInput} tabIndex={-1}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* DROPDOWN */}
              {dropOpen && suggestions.length > 0 && (
                <div style={S.dropdown}>
                  <div style={S.dropHeader}>
                    {suggestions.length} result{suggestions.length !== 1 ? "s" : ""} · ↑↓ to navigate · Enter to select
                  </div>
                  {suggestions.map((occ, i) => {
                    const cat = CAT_STYLE[occ.category] || CAT_STYLE["Enlisted"];
                    const q = query.trim();
                    return (
                      <div
                        key={`${occ.branch}-${occ.code}-${i}`}
                        style={{ ...S.dropRow, ...(highlightIdx === i ? S.dropRowActive : {}) }}
                        onMouseEnter={() => setHighlightIdx(i)}
                        onMouseDown={e => { e.preventDefault(); selectSuggestion(occ); }}
                      >
                        <div style={S.dropMain}>
                          <span style={S.dropCode}>{highlight(occ.code, q)}</span>
                          <span style={S.dropSep}>·</span>
                          <span style={S.dropTitle}>{highlight(occ.title, q)}</span>
                        </div>
                        <div style={S.dropTags}>
                          <span style={{ ...S.tag, background: "rgba(201,168,76,0.08)", borderColor: "rgba(201,168,76,0.18)", color: "#8FA3BE" }}>
                            {occ.branch}
                          </span>
                          <span style={{ ...S.tag, background: cat.bg, borderColor: cat.border, color: cat.color }}>
                            {occ.category}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div style={S.dropFooter}>
                    Showing top {suggestions.length} matches · All six branches included
                  </div>
                </div>
              )}
            </div>

            {/* TRANSLATE BUTTON */}
            <button
              style={{ ...S.translateBtn, ...(!selected || loading ? S.translateBtnOff : {}) }}
              onClick={() => handleTranslate()}
              disabled={!selected || loading}
            >
              {loading ? <Spinner /> : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  Translate
                </>
              )}
            </button>
          </div>

          {/* SELECTED PILL */}
          {selected && (
            <div style={S.selectedRow}>
              <span style={S.selectedDot} />
              <span style={S.selectedText}>
                <strong style={{ color: "#C9A84C" }}>{selected.code}</strong>
                &nbsp;·&nbsp;{selected.title}&nbsp;·&nbsp;{selected.branch}&nbsp;·&nbsp;
                <span style={{ color: (CAT_STYLE[selected.category] || {}).color || "#C9A84C" }}>
                  {selected.category}
                </span>
              </span>
            </div>
          )}

          {/* QUICK EXAMPLES */}
          <div style={S.examplesRow}>
            <span style={S.examplesLabel}>Quick examples:</span>
            {EXAMPLES.map(ex => (
              <button key={ex.code} style={{ ...S.exampleChip, ...(loading ? { opacity: 0.5, cursor: "wait" } : {}) }} disabled={loading} onClick={() => handleExample(ex)}>
                {ex.code} · {ex.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── ERROR ── */}
      {error && (
        <div style={S.errorMsg}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {/* ── LOADING ── */}
      {loading && (
        <div style={S.loadingBlock}>
          <div style={S.loadingRing} />
          <p style={S.loadingText}>Translating military experience into civilian intelligence…</p>
        </div>
      )}

      {/* ── RESULTS ── */}
      {result && !loading && (
        <div ref={resultRef} style={S.results}>

          {/* Profile header */}
          <div style={S.profileHeader}>
            <div style={S.profileLeft}>
              <div style={S.chipRow}>
                <span style={S.codeChip}>{result.code}</span>
                <span style={{ ...S.chip, background: "rgba(99,179,237,0.1)", borderColor: "rgba(99,179,237,0.25)", color: "#63b3ed" }}>{result.branch}</span>
                {selected?.category && (
                  <span style={{ ...S.chip, background: (CAT_STYLE[selected.category] || {}).bg, borderColor: (CAT_STYLE[selected.category] || {}).border, color: (CAT_STYLE[selected.category] || {}).color }}>
                    {selected.category}
                  </span>
                )}
              </div>
              <h2 style={S.profileTitle}>{result.title}</h2>
              <p style={S.profileSummary}>{result.summary}</p>
            </div>
            <div style={S.skillCols}>
              <SkillGroup label="Possible hard skills to confirm"     items={result.hardSkills} color="#C9A84C" />
              <SkillGroup label="Possible tools to confirm"  items={result.tools}      color="#63b3ed" />
              <SkillGroup label="Possible soft skills to confirm"      items={result.softSkills} color="#a78bfa" />
            </div>
          </div>

          <p style={S.resumeIntro} role="note">AI-generated, unverified role-level suggestions. These do not establish your skills, eligibility or employability. Confirm every claim against your own experience. Employer examples are research leads, not partners or current openings.</p>
          {/* Tabs */}
          <div style={S.tabBar}>
            {tabs.map(t => (
              <button key={t.id} style={{ ...S.tabBtn, ...(activeTab === t.id ? S.tabActive : {}) }} onClick={() => setActiveTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── TAB CONTENT ── */}

          {activeTab === "jobs" && (
            <div style={S.jobGrid}>
              {result.civilianJobs?.map((job, i) => (
                <div key={i} style={S.jobCard}>
                  <div style={S.jobTop}>
                    <div style={{ flex: 1 }}>
                      <div style={S.jobTitle}>{job.title}</div>
                      <div style={S.jobDesc}>{job.description}</div>
                    </div>
                  </div>
                  <p style={S.certMeta}>Career direction to research — individual qualifications not assessed.</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === "certs" && (
            <div style={S.certList}>
              {result.certifications?.map((cert, i) => (
                <div key={i} style={S.certCard}>
                  <div style={S.certNum}>{String(i + 1).padStart(2, "0")}</div>
                  <div style={{ flex: 1 }}>
                    <div style={S.certTop}>
                      <span style={S.certName}>{cert.name}</span>
                    </div>
                    <p style={S.certWhy}>{cert.why}</p>
                    {cert.sourceUrl === 'https://www.isc2.org/Certifications/Associate' && (
                      <p><a href="https://www.isc2.org/Certifications/Associate" target="_blank" rel="noopener noreferrer" style={{ color: '#C9A84C' }}>Verify pathways with ISC2 (official source)</a></p>
                    )}
                    <p style={S.certMeta}>Verify requirements, costs and timelines with the credential issuer.</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "employers" && (
            <div style={S.empGrid}>
              {result.employers?.map((emp, i) => (
                <div key={i} style={S.empCard}>
                  <div style={S.empAvatar}>{emp.name.slice(0, 2).toUpperCase()}</div>
                  <div>
                    <div style={S.empName}>{emp.name}</div>
                    <div style={S.empType}>{emp.type}</div>
                    <p style={S.empWhy}>{emp.why}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "resume" && (
            <div>
              <div style={S.resumeIntro}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>Illustrative templates only. Replace placeholders and use only statements that accurately describe your own experience.</span>
              </div>
              {result.resumeBullets?.map((b, i) => (
                <div key={i} style={{ ...S.bulletCard, ...(revealedBullet === i ? S.bulletActive : {}) }} onClick={() => setRevealedBullet(revealedBullet === i ? null : i)}>
                  <div style={S.bulletLabelText}>Military Language</div>
                  <p style={S.bulletBody}>{b.military}</p>
                  {revealedBullet === i ? (
                    <div style={S.bulletReveal}>
                      <div style={{ ...S.bulletLabelText, color: "#C9A84C" }}>✦ Civilian Translation</div>
                      <p style={{ ...S.bulletBody, color: "#E2C06A" }}>{b.civilian}</p>
                      <button style={{ ...S.copyBtn, ...(copied === i ? S.copyDone : {}) }} onClick={e => handleCopy(b.civilian, i, e)}>
                        {copied === i ? "✓ Copied!" : "Copy to Clipboard"}
                      </button>
                    </div>
                  ) : (
                    <div style={S.bulletHint}>Click to translate →</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={S.footer}>
        <span style={{ color: "#C9A84C" }}>VetQualified</span> · Built for those who served 🎖️
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function SkillGroup({ label, items, color }) {
  return (
    <div style={{ marginBottom: "0.75rem" }}>
      <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color, marginBottom: "0.4rem" }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
        {items?.map((s, i) => (
          <span key={i} style={{ padding: "0.22rem 0.6rem", background: "rgba(255,255,255,0.04)", border: `1px solid ${color}30`, borderRadius: 5, fontSize: "0.68rem", fontWeight: 500, color }}>{s}</span>
        ))}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ display: "block" }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.7s" repeatCount="indefinite"/>
      </path>
    </svg>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const S = {
  root: { fontFamily: "'DM Sans', sans-serif", background: "#0B1F3A", minHeight: "100vh", color: "#FFF", position: "relative", overflowX: "hidden" },
  bg: { position: "fixed", inset: 0, background: "radial-gradient(ellipse 70% 50% at 15% 20%,rgba(201,168,76,.07) 0%,transparent 60%),radial-gradient(ellipse 60% 60% at 85% 80%,rgba(18,40,71,.9) 0%,transparent 60%),repeating-linear-gradient(0deg,transparent,transparent 59px,rgba(201,168,76,.03) 59px,rgba(201,168,76,.03) 60px),repeating-linear-gradient(90deg,transparent,transparent 59px,rgba(201,168,76,.03) 59px,rgba(201,168,76,.03) 60px)", pointerEvents: "none", zIndex: 0 },
  header: { position: "relative", zIndex: 1, textAlign: "center", padding: "4rem 2rem 2.5rem", borderBottom: "1px solid rgba(201,168,76,.12)" },
  headerInner: { maxWidth: 620, margin: "0 auto" },
  engineBadge: { display: "inline-block", fontSize: ".62rem", fontWeight: 700, letterSpacing: ".2em", color: "#C9A84C", border: "1px solid rgba(201,168,76,.3)", borderRadius: 4, padding: ".25rem .75rem", marginBottom: "1rem", background: "rgba(201,168,76,.07)" },
  h1: { fontFamily: "'DM Serif Display',serif", fontSize: "clamp(2rem,5vw,3rem)", lineHeight: 1.1, color: "#FFF", letterSpacing: "-.02em", marginBottom: "1rem" },
  h1em: { color: "#C9A84C", fontStyle: "italic" },
  subtitle: { fontSize: ".875rem", color: "#8FA3BE", lineHeight: 1.75, maxWidth: 500, margin: "0 auto" },
  searchSection: { position: "relative", zIndex: 1, maxWidth: 880, margin: "0 auto", padding: "2rem 1.5rem" },
  searchCard: { background: "rgba(255,255,255,.04)", border: "1px solid rgba(201,168,76,.2)", borderRadius: 16, padding: "1.75rem" },
  searchRow: { display: "flex", gap: ".75rem", alignItems: "flex-end", flexWrap: "wrap" },
  inputWrap: { flex: 1, minWidth: 280, position: "relative" },
  inputLabel: { display: "block", fontSize: ".62rem", fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#8FA3BE", marginBottom: ".45rem" },
  inputGroup: { position: "relative", display: "flex", alignItems: "center" },
  searchIconWrap: { position: "absolute", left: 12, pointerEvents: "none", display: "flex", alignItems: "center" },
  input: { width: "100%", background: "rgba(255,255,255,.06)", border: "1px solid rgba(201,168,76,.25)", borderRadius: 10, color: "#FFF", fontFamily: "'DM Sans',sans-serif", fontSize: ".9rem", padding: ".72rem 2.75rem .72rem 2.5rem", outline: "none" },
  clearBtn: { position: "absolute", right: 10, background: "transparent", border: "none", color: "#8FA3BE", cursor: "pointer", display: "flex", alignItems: "center", padding: 4 },
  dropdown: { position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0, background: "#0f2340", border: "1px solid rgba(201,168,76,.28)", borderRadius: 12, overflow: "hidden", zIndex: 300, boxShadow: "0 20px 60px rgba(0,0,0,.6)" },
  dropHeader: { padding: ".45rem 1rem", fontSize: ".62rem", color: "rgba(143,163,190,.6)", borderBottom: "1px solid rgba(255,255,255,.05)", letterSpacing: ".08em" },
  dropRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: ".6rem 1rem", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,.04)", gap: ".75rem" },
  dropRowActive: { background: "rgba(201,168,76,.1)" },
  dropMain: { display: "flex", alignItems: "center", gap: ".4rem", flex: 1, minWidth: 0, overflow: "hidden" },
  dropCode: { fontSize: ".8rem", fontWeight: 800, color: "#FFF", letterSpacing: ".04em", flexShrink: 0 },
  dropSep: { color: "rgba(143,163,190,.4)", flexShrink: 0 },
  dropTitle: { fontSize: ".75rem", color: "#8FA3BE", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  dropTags: { display: "flex", gap: ".3rem", flexShrink: 0 },
  tag: { fontSize: ".58rem", fontWeight: 600, padding: ".15rem .45rem", borderRadius: 4, border: "1px solid", letterSpacing: ".06em", whiteSpace: "nowrap" },
  dropFooter: { padding: ".45rem 1rem", fontSize: ".62rem", color: "rgba(143,163,190,.4)", borderTop: "1px solid rgba(255,255,255,.05)", textAlign: "center" },
  mark: { background: "rgba(201,168,76,.28)", color: "#E2C06A", borderRadius: 2, padding: "0 1px" },
  translateBtn: { display: "flex", alignItems: "center", gap: ".5rem", padding: ".72rem 1.5rem", background: "#C9A84C", border: "none", borderRadius: 10, color: "#0B1F3A", fontFamily: "'DM Sans',sans-serif", fontSize: ".875rem", fontWeight: 800, cursor: "pointer", letterSpacing: ".04em", minWidth: 130, justifyContent: "center" },
  translateBtnOff: { background: "#2d3f56", color: "#8FA3BE", cursor: "not-allowed" },
  selectedRow: { display: "flex", alignItems: "center", gap: ".5rem", marginTop: ".85rem", padding: ".5rem .85rem", background: "rgba(201,168,76,.06)", border: "1px solid rgba(201,168,76,.15)", borderRadius: 8 },
  selectedDot: { display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 0 3px rgba(74,222,128,.2)", flexShrink: 0 },
  selectedText: { fontSize: ".75rem", color: "#CBD5E1" },
  examplesRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: ".45rem", marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid rgba(201,168,76,.1)" },
  examplesLabel: { fontSize: ".7rem", color: "#8FA3BE", marginRight: ".1rem" },
  exampleChip: { padding: ".28rem .7rem", background: "rgba(201,168,76,.07)", border: "1px solid rgba(201,168,76,.2)", borderRadius: 999, color: "#C9A84C", fontSize: ".68rem", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", letterSpacing: ".03em" },
  errorMsg: { position: "relative", zIndex: 1, maxWidth: 880, margin: "0 auto .75rem", padding: "0 1.5rem", display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".8rem", color: "#f87171" },
  loadingBlock: { position: "relative", zIndex: 1, textAlign: "center", padding: "4rem 1rem" },
  loadingRing: { width: 44, height: 44, borderRadius: "50%", border: "3px solid rgba(201,168,76,.15)", borderTopColor: "#C9A84C", margin: "0 auto 1.25rem", animation: "spin .8s linear infinite" },
  loadingText: { fontSize: ".85rem", color: "#8FA3BE" },
  results: { position: "relative", zIndex: 1, maxWidth: 920, margin: "0 auto", padding: "0 1.5rem 2rem" },
  profileHeader: { background: "rgba(255,255,255,.03)", border: "1px solid rgba(201,168,76,.2)", borderRadius: 16, padding: "1.75rem", marginBottom: "1.5rem", display: "flex", gap: "2rem", flexWrap: "wrap" },
  profileLeft: { flex: 1, minWidth: 220 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: ".75rem" },
  codeChip: { fontSize: ".65rem", fontWeight: 700, letterSpacing: ".16em", color: "#C9A84C", background: "rgba(201,168,76,.1)", border: "1px solid rgba(201,168,76,.3)", borderRadius: 4, padding: ".2rem .65rem", textTransform: "uppercase" },
  chip: { fontSize: ".65rem", fontWeight: 700, letterSpacing: ".1em", borderRadius: 4, padding: ".2rem .65rem", border: "1px solid" },
  profileTitle: { fontFamily: "'DM Serif Display',serif", fontSize: "1.5rem", color: "#FFF", letterSpacing: "-.02em", marginBottom: ".6rem", lineHeight: 1.2 },
  profileSummary: { fontSize: ".82rem", color: "#8FA3BE", lineHeight: 1.7, margin: 0 },
  skillCols: { flex: 1, minWidth: 220 },
  tabBar: { display: "flex", gap: ".25rem", borderBottom: "1px solid rgba(201,168,76,.15)", marginBottom: "1.5rem", overflowX: "auto" },
  tabBtn: { padding: ".7rem 1.1rem", background: "transparent", border: "none", borderBottom: "2px solid transparent", color: "#8FA3BE", fontFamily: "'DM Sans',sans-serif", fontSize: ".82rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", marginBottom: -1 },
  tabActive: { color: "#C9A84C", borderBottomColor: "#C9A84C" },
  jobGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" },
  jobCard: { background: "rgba(255,255,255,.03)", border: "1px solid rgba(201,168,76,.15)", borderRadius: 12, padding: "1.25rem" },
  jobTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: ".75rem", marginBottom: ".75rem" },
  jobTitle: { fontFamily: "'DM Serif Display',serif", fontSize: "1rem", color: "#FFF", marginBottom: ".3rem", lineHeight: 1.3 },
  jobDesc: { fontSize: ".72rem", color: "#8FA3BE", lineHeight: 1.5 },
  certList: { display: "flex", flexDirection: "column", gap: ".75rem" },
  certCard: { display: "flex", gap: "1rem", background: "rgba(255,255,255,.03)", border: "1px solid rgba(201,168,76,.15)", borderRadius: 12, padding: "1.25rem", alignItems: "flex-start" },
  certNum: { fontFamily: "'DM Serif Display',serif", fontSize: "1.4rem", color: "rgba(201,168,76,.3)", lineHeight: 1, width: 36, textAlign: "center", flexShrink: 0 },
  certTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".4rem", gap: ".75rem" },
  certName: { fontWeight: 700, fontSize: ".9rem", color: "#FFF" },
  certWhy: { fontSize: ".78rem", color: "#8FA3BE", lineHeight: 1.55, marginBottom: ".6rem" },
  certMeta: { fontSize: ".72rem", color: "#CBD5E1" },
  empGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: "1rem" },
  empCard: { display: "flex", gap: ".9rem", background: "rgba(255,255,255,.03)", border: "1px solid rgba(201,168,76,.15)", borderRadius: 12, padding: "1.25rem", alignItems: "flex-start" },
  empAvatar: { width: 40, height: 40, borderRadius: 8, background: "rgba(201,168,76,.12)", border: "1px solid rgba(201,168,76,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".75rem", fontWeight: 800, color: "#C9A84C", flexShrink: 0, fontFamily: "'DM Serif Display',serif" },
  empName: { fontWeight: 700, fontSize: ".875rem", color: "#FFF", marginBottom: ".15rem" },
  empType: { fontSize: ".67rem", color: "#C9A84C", fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: ".4rem" },
  empWhy: { fontSize: ".75rem", color: "#8FA3BE", lineHeight: 1.5, margin: 0 },
  resumeIntro: { display: "flex", alignItems: "center", gap: ".5rem", padding: ".9rem 1.1rem", background: "rgba(201,168,76,.06)", border: "1px solid rgba(201,168,76,.2)", borderRadius: 10, marginBottom: "1rem", fontSize: ".78rem", color: "#8FA3BE", lineHeight: 1.5 },
  bulletCard: { background: "rgba(255,255,255,.03)", border: "1px solid rgba(201,168,76,.15)", borderRadius: 12, padding: "1.1rem 1.25rem", cursor: "pointer", marginBottom: ".75rem" },
  bulletActive: { borderColor: "rgba(201,168,76,.4)", background: "rgba(201,168,76,.04)" },
  bulletLabelText: { fontSize: ".6rem", fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#8FA3BE", marginBottom: ".35rem" },
  bulletBody: { fontSize: ".85rem", color: "#CBD5E1", lineHeight: 1.6, margin: 0 },
  bulletReveal: { marginTop: ".9rem", paddingTop: ".9rem", borderTop: "1px solid rgba(201,168,76,.2)" },
  bulletHint: { fontSize: ".68rem", color: "rgba(201,168,76,.4)", textAlign: "right", marginTop: ".3rem" },
  copyBtn: { marginTop: ".75rem", padding: ".4rem 1rem", background: "rgba(201,168,76,.12)", border: "1px solid rgba(201,168,76,.3)", borderRadius: 7, color: "#C9A84C", fontFamily: "'DM Sans',sans-serif", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", letterSpacing: ".04em" },
  copyDone: { background: "rgba(74,222,128,.12)", borderColor: "rgba(74,222,128,.3)", color: "#4ade80" },
  footer: { position: "relative", zIndex: 1, textAlign: "center", padding: "2rem", borderTop: "1px solid rgba(201,168,76,.1)", fontSize: ".72rem", color: "#8FA3BE" },
};